/**
 * Advice history panel.
 *
 * Displays a timeline of historical advice records for the matter,
 * showing issue dates, methods, and reserve totals at those points.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Text,
  Spinner,
  MessageBar,
  MessageBarBody,
  makeStyles,
  tokens,
  Divider,
} from '@fluentui/react-components';
import { useGateway } from '../../api/useGateway';
import type { AdviceHistory } from '../../api/gateway-client';

interface AdviceHistoryPanelProps {
  matterId: string | null;
}

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  historyList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  historyItem: {
    display: 'flex',
    flexDirection: 'column',
    padding: '10px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '4px',
    borderLeft: `4px solid ${tokens.colorBrandStroke1}`,
  },
  itemHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '4px',
  },
  itemTitle: {
    fontSize: '13px',
    fontWeight: 600,
  },
  itemDate: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
  },
  itemDetail: {
    fontSize: '12px',
    display: 'flex',
    justifyContent: 'space-between',
  },
  emptyState: {
    textAlign: 'center',
    padding: '24px 12px',
    color: tokens.colorNeutralForeground3,
  },
  reserveValue: {
    fontFamily: 'monospace',
    fontWeight: 600,
  }
});

function formatCurrency(value: number): string {
  return value.toLocaleString('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
  });
}

export const AdviceHistoryPanel: React.FC<AdviceHistoryPanelProps> = ({ matterId }) => {
  const styles = useStyles();
  const gateway = useGateway();
  const [history, setHistory] = useState<AdviceHistory[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    if (!gateway || !matterId) return;

    setIsLoading(true);
    setError(null);

    try {
      const results = await gateway.getAdviceHistory(matterId);
      setHistory(results);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load history');
    } finally {
      setIsLoading(false);
    }
  }, [gateway, matterId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  if (!matterId) {
    return (
      <div className={styles.emptyState}>
        <Text>Select a matter first to view advice history</Text>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Text weight="semibold">Advice History</Text>
      <Divider />

      {isLoading && <Spinner size="small" label="Loading history..." />}

      {error && (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}

      {!isLoading && !error && history.length === 0 && (
        <div className={styles.emptyState}>
          <Text>No completed advice found for this matter</Text>
        </div>
      )}

      {!isLoading && history.length > 0 && (
        <div className={styles.historyList}>
          {history.map((item) => (
            <div key={item.id} className={styles.historyItem}>
              <div className={styles.itemHeader}>
                <Text className={styles.itemTitle}>{item.title}</Text>
                <Text className={styles.itemDate}>
                  {item.dateIssued ? new Date(item.dateIssued).toLocaleDateString() : 'No date'}
                </Text>
              </div>
              <div className={styles.itemDetail}>
                <Text>Method: {item.method || 'Unknown'}</Text>
                <Text className={styles.reserveValue}>
                  {formatCurrency(item.reserveTotal || 0)}
                </Text>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
